import { useEffect, useRef, type ReactNode } from 'react'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded'
import InboxRoundedIcon from '@mui/icons-material/InboxRounded'
import { Button } from './Button'
import {
  EMPTY_HUB_STATE_PRESETS,
  type EmptyHubStatePresetKey,
} from './emptyHubStatePresets'
import './emptyHubState.css'

export type EmptyHubStateVariant = 'default' | 'restricted' | 'search' | 'error'
export type EmptyHubStateAppearance = 'default' | 'card'

export type EmptyHubStateAction = {
  label: string
  onClick: () => void
  icon?: ReactNode
  disabled?: boolean
}

export type EmptyHubStateProps = {
  preset?: EmptyHubStatePresetKey
  variant?: EmptyHubStateVariant
  icon?: ReactNode
  title?: string
  description?: string
  primaryAction?: EmptyHubStateAction
  secondaryAction?: Omit<EmptyHubStateAction, 'icon'>
  helperLinkHref?: string
  className?: string
  children?: ReactNode
  compact?: boolean
  /** Visual chrome is independent from the semantic empty-state variant. */
  appearance?: EmptyHubStateAppearance
  autoFocus?: boolean
}

const variantIcon = (variant: EmptyHubStateVariant): ReactNode => {
  if (variant === 'restricted') return <LockRoundedIcon fontSize="inherit" />
  if (variant === 'search') return <SearchOffRoundedIcon fontSize="inherit" />
  if (variant === 'error') return <ErrorOutlineRoundedIcon fontSize="inherit" />
  return <InboxRoundedIcon fontSize="inherit" />
}

export const EmptyHubState = ({
  preset,
  variant = 'default',
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  helperLinkHref,
  className,
  children,
  compact = false,
  appearance = 'default',
  autoFocus = false,
}: EmptyHubStateProps) => {
  const primaryRef = useRef<HTMLButtonElement | null>(null)
  const presetConfig = preset ? EMPTY_HUB_STATE_PRESETS[preset] : null
  const Icon = presetConfig?.Icon
  const resolvedTitle = title ?? presetConfig?.title ?? 'Nothing here yet'
  const resolvedDescription = description ?? presetConfig?.description
  const resolvedIcon = icon ?? (Icon ? <Icon fontSize="inherit" /> : variantIcon(variant))

  useEffect(() => {
    if (!autoFocus || !primaryAction) return
    primaryRef.current?.focus()
  }, [autoFocus, primaryAction])

  return (
    <section
      className={[
        'emptyHubState',
        `emptyHubState--${variant}`,
        `emptyHubState--${appearance}`,
        compact ? 'emptyHubState--compact' : '',
        className ?? '',
      ].filter(Boolean).join(' ')}
      aria-live="polite"
    >
      <div className="emptyHubState__inner">
        <div className="emptyHubState__glyph" aria-hidden="true">{resolvedIcon}</div>
        <div className="emptyHubState__copy">
          <h2>{resolvedTitle}</h2>
          {resolvedDescription ? <p>{resolvedDescription}</p> : null}
        </div>
        {(primaryAction || secondaryAction || helperLinkHref) ? (
          <div className="emptyHubState__actions">
            {primaryAction ? (
              <Button ref={primaryRef} variant="primary" size="sm" icon={primaryAction.icon} disabled={primaryAction.disabled} onClick={primaryAction.onClick}>
                {primaryAction.label}
              </Button>
            ) : null}
            {secondaryAction ? (
              <Button variant="tertiary" size="sm" disabled={secondaryAction.disabled} onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </Button>
            ) : null}
            {helperLinkHref ? (
              <a className="emptyHubState__helperLink" href={helperLinkHref}>
                Help
              </a>
            ) : null}
          </div>
        ) : null}
        {children ? <div className="emptyHubState__custom">{children}</div> : null}
      </div>
    </section>
  )
}
